import accountingService from './accountingService';
import accountingDataService from './accountingDataService';
import financialCalculationService from './financialCalculationService';
import comprehensiveAccountingDataService from './comprehensiveAccountingDataService';
import comprehensiveJournalService from './comprehensiveJournalService';
import { getStudentBill, calculateTotalFeesDue, getOtherChargesFromBill } from '../utils/billUtils';

class RealDataAccountingService {
  // Initialize Chart of Accounts with real data mapping
  async initializeChartOfAccounts(schoolID, userId, userName) {
    const defaultAccounts = [
      // ASSETS (1000-1999)
      { 
        accountCode: '1000', 
        accountName: 'Cash and Cash Equivalents', 
        accountType: 'Asset', 
        accountSubType: 'Current Asset', 
        normalBalance: 'Debit',
        description: 'Cash on hand and in bank accounts. Includes all liquid funds available for daily operations and expenses.'
      },
      { 
        accountCode: '1100', 
        accountName: 'Accounts Receivable - School Fees', 
        accountType: 'Asset', 
        accountSubType: 'Current Asset', 
        normalBalance: 'Debit',
        description: 'Amounts owed by students for school fees that have been billed but not yet collected. Represents expected cash inflows from student fee payments.'
      },
      { 
        accountCode: '1110', 
        accountName: 'Accounts Receivable - Outstanding Fees', 
        accountType: 'Asset', 
        accountSubType: 'Current Asset', 
        normalBalance: 'Debit',
        description: 'Overdue school fees from previous terms that remain unpaid. These are fees that are past their due date and require collection efforts.'
      },
      { 
        accountCode: '1120', 
        accountName: 'Accounts Receivable - Non-Billable Charges', 
        accountType: 'Asset', 
        accountSubType: 'Current Asset', 
        normalBalance: 'Debit',
        description: 'Amounts owed for additional charges that are not part of regular school fees, such as special programs or services.'
      },
      { 
        accountCode: '1200', 
        accountName: 'Accounts Receivable - Canteen Fees', 
        accountType: 'Asset', 
        accountSubType: 'Current Asset', 
        normalBalance: 'Debit',
        description: 'Amounts owed by students and staff for canteen purchases made on credit. These are typically short-term receivables.'
      },
      { 
        accountCode: '1300', 
        accountName: 'Inventory - Canteen Products', 
        accountType: 'Asset', 
        accountSubType: 'Current Asset', 
        normalBalance: 'Debit',
        description: 'Value of canteen products, food items, and supplies currently in stock. This represents goods available for sale.'
      },
      { 
        accountCode: '1400', 
        accountName: 'Prepaid Expenses', 
        accountType: 'Asset', 
        accountSubType: 'Current Asset', 
        normalBalance: 'Debit',
        description: 'Expenses paid in advance, such as prepaid rent, insurance premiums, or subscriptions that will be expensed in future periods.'
      },
      { 
        accountCode: '1500', 
        accountName: 'Land and Buildings', 
        accountType: 'Asset', 
        accountSubType: 'Fixed Asset', 
        normalBalance: 'Debit',
        description: 'School buildings, land, and permanent structures owned by the school. These are long-term assets used in operations.'
      },
      { 
        accountCode: '1600', 
        accountName: 'Equipment', 
        accountType: 'Asset', 
        accountSubType: 'Fixed Asset', 
        normalBalance: 'Debit',
        description: 'School equipment including computers, projectors, laboratory equipment, and other educational equipment with useful life exceeding one year.'
      },
      { 
        accountCode: '1700', 
        accountName: 'Furniture and Fixtures', 
        accountType: 'Asset', 
        accountSubType: 'Fixed Asset', 
        normalBalance: 'Debit',
        description: 'Desks, chairs, tables, cabinets, and other furniture used in school operations. These are long-term assets subject to depreciation.'
      },
      { 
        accountCode: '1800', 
        accountName: 'Accumulated Depreciation', 
        accountType: 'Asset', 
        accountSubType: 'Fixed Asset', 
        normalBalance: 'Credit',
        description: 'Total depreciation expense accumulated over time for fixed assets. This is a contra-asset account that reduces the book value of fixed assets.'
      },
      
      // LIABILITIES (2000-2999)
      { 
        accountCode: '2000', 
        accountName: 'Accounts Payable', 
        accountType: 'Liability', 
        accountSubType: 'Current Liability', 
        normalBalance: 'Credit',
        description: 'Amounts owed to suppliers and vendors for goods and services received but not yet paid. These are short-term obligations.'
      },
      { 
        accountCode: '2100', 
        accountName: 'Accrued Salaries', 
        accountType: 'Liability', 
        accountSubType: 'Current Liability', 
        normalBalance: 'Credit',
        description: 'Salaries and wages earned by employees but not yet paid. This represents the school\'s obligation to pay employees for work performed.'
      },
      { 
        accountCode: '2200', 
        accountName: 'Accrued Expenses', 
        accountType: 'Liability', 
        accountSubType: 'Current Liability', 
        normalBalance: 'Credit',
        description: 'Expenses incurred but not yet paid, such as utilities, rent, or other services received but not yet invoiced or paid.'
      },
      { 
        accountCode: '2300', 
        accountName: 'Short-term Debt', 
        accountType: 'Liability', 
        accountSubType: 'Current Liability', 
        normalBalance: 'Credit',
        description: 'Loans and borrowings that are due within one year. This includes short-term bank loans and credit facilities.'
      },
      { 
        accountCode: '2400', 
        accountName: 'Long-term Debt', 
        accountType: 'Liability', 
        accountSubType: 'Long Term Liability', 
        normalBalance: 'Credit',
        description: 'Loans and borrowings with repayment terms exceeding one year. This includes long-term bank loans and financing arrangements.'
      },
      
      // EQUITY (3000-3999)
      { 
        accountCode: '3000', 
        accountName: 'Capital', 
        accountType: 'Equity', 
        accountSubType: 'Capital', 
        normalBalance: 'Credit',
        description: 'Initial capital investment and contributions made to the school. This represents the owner\'s or founder\'s equity in the school.'
      },
      { 
        accountCode: '3100', 
        accountName: 'Retained Earnings', 
        accountType: 'Equity', 
        accountSubType: 'Retained Earnings', 
        normalBalance: 'Credit',
        description: 'Accumulated net income retained in the school after all expenses and distributions. This represents profits reinvested in the school.'
      },
      
      // REVENUE (4000-4999)
      { 
        accountCode: '4000', 
        accountName: 'School Fees Revenue', 
        accountType: 'Revenue', 
        accountSubType: 'Operating Revenue', 
        normalBalance: 'Credit',
        description: 'Revenue from student school fees collected during the period. This is the primary source of operating revenue for the school.'
      },
      { 
        accountCode: '4100', 
        accountName: 'Canteen Revenue', 
        accountType: 'Revenue', 
        accountSubType: 'Operating Revenue', 
        normalBalance: 'Credit',
        description: 'Revenue from canteen sales to students and staff. Includes sales of food, beverages, and other items from the school canteen.'
      },
      { 
        accountCode: '4200', 
        accountName: 'Admission Fee Revenue', 
        accountType: 'Revenue', 
        accountSubType: 'Operating Revenue', 
        normalBalance: 'Credit',
        description: 'One-time admission fees collected from new students upon enrollment. This is non-recurring revenue.'
      },
      { 
        accountCode: '4210', 
        accountName: 'Printing Fee Revenue', 
        accountType: 'Revenue', 
        accountSubType: 'Operating Revenue', 
        normalBalance: 'Credit',
        description: 'Revenue from printing and photocopying services provided to students and staff.'
      },
      { 
        accountCode: '4220', 
        accountName: 'Books Revenue', 
        accountType: 'Revenue', 
        accountSubType: 'Operating Revenue', 
        normalBalance: 'Credit',
        description: 'Revenue from sales of textbooks, workbooks, and other educational materials to students.'
      },
      { 
        accountCode: '4230', 
        accountName: 'Uniform Revenue', 
        accountType: 'Revenue', 
        accountSubType: 'Operating Revenue', 
        normalBalance: 'Credit',
        description: 'Revenue from sales of school uniforms and related items to students.'
      },
      { 
        accountCode: '4240', 
        accountName: 'Other Charges Revenue', 
        accountType: 'Revenue', 
        accountSubType: 'Operating Revenue', 
        normalBalance: 'Credit',
        description: 'Revenue from miscellaneous charges and fees not categorized elsewhere, such as activity fees or special program fees.'
      },
      { 
        accountCode: '4250', 
        accountName: 'Daily Charges Revenue', 
        accountType: 'Revenue', 
        accountSubType: 'Operating Revenue', 
        normalBalance: 'Credit',
        description: 'Revenue from daily charges applied to students, such as daily meal charges or daily activity fees.'
      },
      { 
        accountCode: '4260', 
        accountName: 'Weekly Charges Revenue', 
        accountType: 'Revenue', 
        accountSubType: 'Operating Revenue', 
        normalBalance: 'Credit',
        description: 'Revenue from weekly charges applied to students, such as weekly meal plans or weekly activity fees.'
      },
      { 
        accountCode: '4270', 
        accountName: 'Monthly Charges Revenue', 
        accountType: 'Revenue', 
        accountSubType: 'Operating Revenue', 
        normalBalance: 'Credit',
        description: 'Revenue from monthly charges applied to students, such as monthly meal plans or monthly service fees.'
      },
      { 
        accountCode: '4280', 
        accountName: 'Termly Charges Revenue', 
        accountType: 'Revenue', 
        accountSubType: 'Operating Revenue', 
        normalBalance: 'Credit',
        description: 'Revenue from termly charges applied to students, such as termly meal plans or termly program fees.'
      },
      { 
        accountCode: '4300', 
        accountName: 'Discounts Given', 
        accountType: 'Revenue', 
        accountSubType: 'Contra Revenue', 
        normalBalance: 'Debit',
        description: 'Discounts and fee reductions granted to students. This is a contra-revenue account that reduces total revenue.'
      },
      
      // EXPENSES (5000-5999)
      { 
        accountCode: '5000', 
        accountName: 'Salaries and Wages', 
        accountType: 'Expense', 
        accountSubType: 'Operating Expense', 
        normalBalance: 'Debit',
        description: 'Total salaries and wages paid to teachers, administrative staff, and other employees. This is typically the largest operating expense.'
      },
      { 
        accountCode: '5100', 
        accountName: 'Administrative Expenses', 
        accountType: 'Expense', 
        accountSubType: 'Administrative Expense', 
        normalBalance: 'Debit',
        description: 'General administrative costs including office supplies, communication expenses, management fees, and other administrative overhead.'
      },
      { 
        accountCode: '5200', 
        accountName: 'Utilities', 
        accountType: 'Expense', 
        accountSubType: 'Operating Expense', 
        normalBalance: 'Debit',
        description: 'Electricity, water, internet, telephone, and other utility expenses necessary for school operations.'
      },
      { 
        accountCode: '5300', 
        accountName: 'Maintenance and Repairs', 
        accountType: 'Expense', 
        accountSubType: 'Operating Expense', 
        normalBalance: 'Debit',
        description: 'Costs for maintaining and repairing school facilities, buildings, equipment, and infrastructure.'
      },
      { 
        accountCode: '5400', 
        accountName: 'Professional Services', 
        accountType: 'Expense', 
        accountSubType: 'Operating Expense', 
        normalBalance: 'Debit',
        description: 'Fees paid to external professionals such as accountants, lawyers, consultants, and other service providers.'
      },
      { 
        accountCode: '5500', 
        accountName: 'Office Supplies', 
        accountType: 'Expense', 
        accountSubType: 'Operating Expense', 
        normalBalance: 'Debit',
        description: 'Cost of office supplies, stationery, printing materials, and other consumables used in daily operations.'
      },
      { 
        accountCode: '5600', 
        accountName: 'Transportation', 
        accountType: 'Expense', 
        accountSubType: 'Operating Expense', 
        normalBalance: 'Debit',
        description: 'Transportation costs including vehicle maintenance, fuel, driver salaries, and other transportation-related expenses.'
      },
      { 
        accountCode: '5700', 
        accountName: 'Insurance', 
        accountType: 'Expense', 
        accountSubType: 'Operating Expense', 
        normalBalance: 'Debit',
        description: 'Insurance premiums paid for property, liability, health, and other insurance coverage for the school.'
      },
      { 
        accountCode: '5800', 
        accountName: 'Depreciation Expense', 
        accountType: 'Expense', 
        accountSubType: 'Operating Expense', 
        normalBalance: 'Debit',
        description: 'Depreciation expense for fixed assets such as buildings, equipment, and furniture. This allocates the cost of assets over their useful life.'
      }
    ];

    // Get existing accounts to avoid duplicates
    let existingAccounts = [];
    try {
      existingAccounts = await accountingService.getChartOfAccounts(schoolID);
    } catch (error) {
      console.log('Could not fetch existing accounts, proceeding with creation:', error);
    }
    
    const existingCodes = new Set(existingAccounts.map(acc => acc.accountCode));
    const createdAccounts = [];
    const skippedAccounts = [];
    
    for (const account of defaultAccounts) {
      // Skip if account code already exists
      if (existingCodes.has(account.accountCode)) {
        skippedAccounts.push(account.accountCode);
        console.log(`Skipping account ${account.accountCode} - already exists`);
        continue;
      }
      
      try {
        const accountData = {
          ...account,
          schoolID,
          createdBy: userId,
          userName: userName || 'System'
        };
        const createdAccount = await accountingService.createAccount(accountData);
        createdAccounts.push(createdAccount);
        console.log(`✅ Created account: ${account.accountCode} - ${account.accountName}`);
      } catch (error) {
        console.error(`❌ Error creating account ${account.accountCode}:`, error);
        // If it's a duplicate error, add to skipped
        const errorMsg = error.response?.data?.error || 
                        error.response?.data?.message || 
                        error.message || '';
        if (errorMsg.includes('duplicate') || 
            errorMsg.includes('unique') ||
            errorMsg.includes('already exists') ||
            errorMsg.includes('E11000')) { // MongoDB duplicate key error code
          skippedAccounts.push(account.accountCode);
          console.log(`⏭️  Skipped account ${account.accountCode} - already exists`);
        } else {
          // Log unexpected errors but continue with other accounts
          console.error(`⚠️  Unexpected error for account ${account.accountCode}:`, errorMsg);
        }
      }
    }

    return {
      created: createdAccounts,
      skipped: skippedAccounts,
      totalCreated: createdAccounts.length,
      totalSkipped: skippedAccounts.length
    };
  }

  // Generate Trial Balance from real data
  async generateRealTrialBalance(schoolID, startDate, endDate, reduxData) {
    try {
      console.log('Generating trial balance with Redux data:', reduxData);
      
      // First, try to get Chart of Accounts from backend if available (with timeout)
      let accounts = [];
      try {
        // Add timeout to prevent hanging
        const chartTimeout = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Chart of Accounts fetch timed out')), 5000);
        });
        
        const chartResponse = await Promise.race([
          accountingService.getChartOfAccounts(schoolID),
          chartTimeout
        ]);
        
        if (chartResponse && Array.isArray(chartResponse) && chartResponse.length > 0) {
          accounts = chartResponse.map(acc => ({
            accountCode: acc.accountCode,
            accountName: acc.accountName,
            accountType: acc.accountType,
            normalBalance: acc.normalBalance || (acc.accountType === 'Asset' || acc.accountType === 'Expense' ? 'Debit' : 'Credit'),
            description: acc.description || ''
          }));
          console.log('Using Chart of Accounts from database:', accounts.length, 'accounts');
        } else {
          // Fallback to creating from Redux data
          accounts = this.createChartOfAccountsFromReduxData(reduxData);
          console.log('Using Chart of Accounts from Redux data:', accounts.length, 'accounts');
        }
      } catch (err) {
        console.warn('Could not fetch Chart of Accounts from database, using Redux data:', err.message || err);
        accounts = this.createChartOfAccountsFromReduxData(reduxData);
      }
      
      // Get all financial data
      const financialData = await accountingDataService.getAllFinancialData(
        schoolID, 
        new Date(startDate), 
        new Date(endDate), 
        reduxData
      );

      console.log('Financial data processed:', financialData);
      
      // Calculate account balances from real data
      const trialBalance = accounts.map(account => {
        const balance = this.calculateAccountBalanceFromRealData(account, financialData);
        
        // Apply normal balance rules
        const normalBalance = account.normalBalance || 
          (account.accountType === 'Asset' || account.accountType === 'Expense' ? 'Debit' : 'Credit');
        
        // For trial balance, show debit and credit columns correctly
        let debitBalance = 0;
        let creditBalance = 0;
        
        if (normalBalance === 'Debit') {
          // Asset/Expense accounts: show net as debit, negative as credit
          const net = balance.debit - balance.credit;
          debitBalance = Math.max(0, net);
          creditBalance = Math.max(0, -net);
        } else {
          // Liability/Equity/Revenue accounts: show net as credit, negative as debit
          const net = balance.credit - balance.debit;
          creditBalance = Math.max(0, net);
          debitBalance = Math.max(0, -net);
        }
        
        return {
          accountCode: account.accountCode,
          accountName: account.accountName,
          accountType: account.accountType,
          debitBalance: debitBalance,
          creditBalance: creditBalance,
          normalBalance: normalBalance
        };
      }).filter(account => account.debitBalance > 0 || account.creditBalance > 0); // Only show accounts with balances

      // Calculate totals
      const totalDebits = trialBalance.reduce((sum, account) => sum + account.debitBalance, 0);
      const totalCredits = trialBalance.reduce((sum, account) => sum + account.creditBalance, 0);

      // If no real data, create sample data for demonstration
      if (trialBalance.length === 0 || (totalDebits === 0 && totalCredits === 0)) {
        console.log('No real data found, creating sample trial balance');
        return this.createSampleTrialBalance(startDate, endDate);
      }

      const result = {
        trialBalance,
        period: { startDate, endDate },
        generatedAt: new Date(),
        totalDebits,
        totalCredits,
        isBalanced: Math.abs(totalDebits - totalCredits) < 0.01,
        difference: Math.abs(totalDebits - totalCredits)
      };

      console.log('Trial balance result:', result);
      console.log(`Trial Balance ${result.isBalanced ? 'BALANCED' : 'NOT BALANCED'}. Difference: ${result.difference.toFixed(2)}`);
      
      return result;
    } catch (error) {
      console.error('Error generating real trial balance:', error);
      // Return sample data if there's an error
      return this.createSampleTrialBalance(startDate, endDate);
    }
  }

  // Create sample trial balance for demonstration
  createSampleTrialBalance(startDate, endDate) {
    const sampleTrialBalance = [
      { accountCode: '1000', accountName: 'Cash and Cash Equivalents', accountType: 'Asset', debitBalance: 50000, creditBalance: 0 },
      { accountCode: '1100', accountName: 'Accounts Receivable', accountType: 'Asset', debitBalance: 25000, creditBalance: 0 },
      { accountCode: '1500', accountName: 'Fixed Assets', accountType: 'Asset', debitBalance: 100000, creditBalance: 0 },
      { accountCode: '2000', accountName: 'Accounts Payable', accountType: 'Liability', debitBalance: 0, creditBalance: 15000 },
      { accountCode: '3000', accountName: 'Owner\'s Equity', accountType: 'Equity', debitBalance: 0, creditBalance: 100000 },
      { accountCode: '4000', accountName: 'School Fees Revenue', accountType: 'Revenue', debitBalance: 0, creditBalance: 80000 },
      { accountCode: '4100', accountName: 'Canteen Revenue', accountType: 'Revenue', debitBalance: 0, creditBalance: 15000 },
      { accountCode: '5000', accountName: 'Salaries and Wages', accountType: 'Expense', debitBalance: 40000, creditBalance: 0 },
      { accountCode: '5100', accountName: 'Operating Expenses', accountType: 'Expense', debitBalance: 25000, creditBalance: 0 }
    ];

    return {
      trialBalance: sampleTrialBalance,
      period: { startDate, endDate },
      generatedAt: new Date(),
      totalDebits: sampleTrialBalance.reduce((sum, account) => sum + account.debitBalance, 0),
      totalCredits: sampleTrialBalance.reduce((sum, account) => sum + account.creditBalance, 0),
      isSampleData: true
    };
  }

  // Create chart of accounts from Redux data
  createChartOfAccountsFromReduxData(reduxData) {
    return [
      // ASSETS (1000-1999) - Debit Normal Balance
      {
        accountCode: '1000',
        accountName: 'Cash and Cash Equivalents',
        accountType: 'Asset',
        normalBalance: 'Debit',
        description: 'Cash on hand and in bank accounts'
      },
      {
        accountCode: '1100',
        accountName: 'Accounts Receivable - School Fees',
        accountType: 'Asset',
        normalBalance: 'Debit',
        description: 'Outstanding school fees from students'
      },
      {
        accountCode: '1110',
        accountName: 'Accounts Receivable - Outstanding Fees',
        accountType: 'Asset',
        normalBalance: 'Debit',
        description: 'Overdue school fees from previous terms'
      },
      {
        accountCode: '1120',
        accountName: 'Accounts Receivable - Non-Billable Charges',
        accountType: 'Asset',
        normalBalance: 'Debit',
        description: 'Amounts owed for additional charges'
      },
      {
        accountCode: '1200',
        accountName: 'Accounts Receivable - Canteen Fees',
        accountType: 'Asset',
        normalBalance: 'Debit',
        description: 'Outstanding canteen fees'
      },
      {
        accountCode: '1300',
        accountName: 'Inventory - Canteen Products',
        accountType: 'Asset',
        normalBalance: 'Debit',
        description: 'Value of canteen products in stock'
      },
      {
        accountCode: '1400',
        accountName: 'Prepaid Expenses',
        accountType: 'Asset',
        normalBalance: 'Debit',
        description: 'Expenses paid in advance'
      },
      {
        accountCode: '1500',
        accountName: 'Land and Buildings',
        accountType: 'Asset',
        normalBalance: 'Debit',
        description: 'School buildings and land'
      },
      {
        accountCode: '1600',
        accountName: 'Equipment',
        accountType: 'Asset',
        normalBalance: 'Debit',
        description: 'School equipment'
      },
      {
        accountCode: '1700',
        accountName: 'Furniture and Fixtures',
        accountType: 'Asset',
        normalBalance: 'Debit',
        description: 'School furniture'
      },
      {
        accountCode: '1800',
        accountName: 'Accumulated Depreciation',
        accountType: 'Asset',
        normalBalance: 'Credit', // Contra-asset account
        description: 'Total depreciation accumulated'
      },
      
      // LIABILITIES (2000-2999) - Credit Normal Balance
      {
        accountCode: '2000',
        accountName: 'Accounts Payable',
        accountType: 'Liability',
        normalBalance: 'Credit',
        description: 'Outstanding bills and expenses'
      },
      {
        accountCode: '2100',
        accountName: 'Accrued Salaries',
        accountType: 'Liability',
        normalBalance: 'Credit',
        description: 'Salaries owed to staff'
      },
      {
        accountCode: '2200',
        accountName: 'Accrued Expenses',
        accountType: 'Liability',
        normalBalance: 'Credit',
        description: 'Expenses incurred but not yet paid'
      },
      {
        accountCode: '2300',
        accountName: 'Short-term Debt',
        accountType: 'Liability',
        normalBalance: 'Credit',
        description: 'Short-term loans and borrowings'
      },
      {
        accountCode: '2400',
        accountName: 'Long-term Debt',
        accountType: 'Liability',
        normalBalance: 'Credit',
        description: 'Long-term loans and borrowings'
      },
      
      // EQUITY (3000-3999) - Credit Normal Balance
      {
        accountCode: '3000',
        accountName: 'Capital',
        accountType: 'Equity',
        normalBalance: 'Credit',
        description: 'Owner\'s capital investment'
      },
      {
        accountCode: '3100',
        accountName: 'Retained Earnings',
        accountType: 'Equity',
        normalBalance: 'Credit',
        description: 'Accumulated net income'
      },
      
      // REVENUE (4000-4999) - Credit Normal Balance
      {
        accountCode: '4000',
        accountName: 'School Fees Revenue',
        accountType: 'Revenue',
        normalBalance: 'Credit',
        description: 'Revenue from school fees'
      },
      {
        accountCode: '4100',
        accountName: 'Canteen Revenue',
        accountType: 'Revenue',
        normalBalance: 'Credit',
        description: 'Revenue from canteen sales'
      },
      {
        accountCode: '4200',
        accountName: 'Admission Fee Revenue',
        accountType: 'Revenue',
        normalBalance: 'Credit',
        description: 'Revenue from admission fees'
      },
      {
        accountCode: '4210',
        accountName: 'Printing Fee Revenue',
        accountType: 'Revenue',
        normalBalance: 'Credit',
        description: 'Revenue from printing fees'
      },
      {
        accountCode: '4220',
        accountName: 'Books Revenue',
        accountType: 'Revenue',
        normalBalance: 'Credit',
        description: 'Revenue from book sales'
      },
      {
        accountCode: '4230',
        accountName: 'Uniform Revenue',
        accountType: 'Revenue',
        normalBalance: 'Credit',
        description: 'Revenue from uniform sales'
      },
      {
        accountCode: '4240',
        accountName: 'Other Charges Revenue',
        accountType: 'Revenue',
        normalBalance: 'Credit',
        description: 'Revenue from other charges'
      },
      {
        accountCode: '4250',
        accountName: 'Daily Charges Revenue',
        accountType: 'Revenue',
        normalBalance: 'Credit',
        description: 'Revenue from daily charges'
      },
      {
        accountCode: '4260',
        accountName: 'Weekly Charges Revenue',
        accountType: 'Revenue',
        normalBalance: 'Credit',
        description: 'Revenue from weekly charges'
      },
      {
        accountCode: '4270',
        accountName: 'Monthly Charges Revenue',
        accountType: 'Revenue',
        normalBalance: 'Credit',
        description: 'Revenue from monthly charges'
      },
      {
        accountCode: '4280',
        accountName: 'Termly Charges Revenue',
        accountType: 'Revenue',
        normalBalance: 'Credit',
        description: 'Revenue from termly charges'
      },
      {
        accountCode: '4300',
        accountName: 'Discounts Given',
        accountType: 'Revenue',
        normalBalance: 'Debit', // Contra-revenue account
        description: 'Discounts provided to students'
      },
      
      // EXPENSES (5000-5999) - Debit Normal Balance
      {
        accountCode: '5000',
        accountName: 'Salaries and Wages',
        accountType: 'Expense',
        normalBalance: 'Debit',
        description: 'Staff salaries and benefits'
      },
      {
        accountCode: '5100',
        accountName: 'Administrative Expenses',
        accountType: 'Expense',
        normalBalance: 'Debit',
        description: 'General administrative expenses'
      },
      {
        accountCode: '5200',
        accountName: 'Utilities',
        accountType: 'Expense',
        normalBalance: 'Debit',
        description: 'Utility expenses'
      },
      {
        accountCode: '5300',
        accountName: 'Maintenance and Repairs',
        accountType: 'Expense',
        normalBalance: 'Debit',
        description: 'Maintenance and repair expenses'
      },
      {
        accountCode: '5400',
        accountName: 'Professional Services',
        accountType: 'Expense',
        normalBalance: 'Debit',
        description: 'Professional service expenses'
      },
      {
        accountCode: '5500',
        accountName: 'Office Supplies',
        accountType: 'Expense',
        normalBalance: 'Debit',
        description: 'Office supply expenses'
      },
      {
        accountCode: '5600',
        accountName: 'Transportation',
        accountType: 'Expense',
        normalBalance: 'Debit',
        description: 'Transportation expenses'
      },
      {
        accountCode: '5700',
        accountName: 'Insurance',
        accountType: 'Expense',
        normalBalance: 'Debit',
        description: 'Insurance expenses'
      },
      {
        accountCode: '5800',
        accountName: 'Depreciation Expense',
        accountType: 'Expense',
        normalBalance: 'Debit',
        description: 'Depreciation expense for fixed assets'
      }
    ];
  }

  // Calculate account balance from real financial data
  calculateAccountBalanceFromRealData(account, financialData) {
    // financialData is already filtered by schoolID and date range from getAllFinancialData
    const { studentFinance, canteenFees, salaries, expenditure, sales, assets, products } = financialData;
    
    // Debug logging
    console.log('Calculating balance for account:', account.accountCode, account.accountName);
    console.log('Financial data available:', {
      studentFinance: studentFinance ? 'Yes' : 'No',
      canteenFees: canteenFees ? 'Yes' : 'No',
      salaries: salaries ? 'Yes' : 'No',
      expenditure: expenditure ? 'Yes' : 'No',
      sales: sales ? 'Yes' : 'No',
      assets: assets ? 'Yes' : 'No',
      products: products ? 'Yes' : 'No'
    });
    
    switch (account.accountCode) {
      // Cash and Cash Equivalents - Calculate from actual inflows and outflows
      // Uses aggregated, filtered totals from accountingDataService (already filtered by schoolID and date range)
      case '1000': {
        // RECEIPTS (increase cash) - actual cash receipts in the selected period
        const schoolFeesCollected = financialData?.studentFinance?.paidFees || 0;
        const canteenFeesCollected = financialData?.canteenFees?.totalRevenue || 0;
        const salesRevenue = financialData?.sales?.totalRevenue || 0;
        const totalReceipts = schoolFeesCollected + canteenFeesCollected + salesRevenue;

        // PAYMENTS (decrease cash) - actual cash outflows in the selected period
        const totalExpenses = financialData?.expenditure?.totalExpenses || 0;
        const salariesPaid = financialData?.salaries?.totalSalaries || 0;
        const totalPayments = totalExpenses + salariesPaid;

        // Net cash from period transactions
        // Note: In a complete system, you'd add opening cash balance here
        // For trial balance, we show net cash from the period
        const netCash = totalReceipts - totalPayments;

        // Return as debit balance (positive) or credit balance (negative)
        return { 
          debit: Math.max(0, netCash), 
          credit: Math.max(0, -netCash) 
        };
      }

      // Accounts Receivable - School Fees
      case '1100':
        const schoolFeesOutstanding = studentFinance?.outstandingFees || 0;
        return { debit: schoolFeesOutstanding, credit: 0 };

      // Accounts Receivable - Outstanding Fees
      case '1110':
        // Calculate from students with overdue balances
        const overdueFees = studentFinance?.students?.reduce((sum, student) => {
          if (student.finance && student.finance.length > 0) {
            const latestFinance = student.finance[student.finance.length - 1];
            const balance = parseFloat(latestFinance.balance) || 0;
            const arrears = parseFloat(latestFinance.Arrears) || 0;
            return sum + (balance > 0 ? balance : 0) + (arrears > 0 ? arrears : 0);
          }
          return sum;
        }, 0) || 0;
        return { debit: overdueFees, credit: 0 };

      // Accounts Receivable - Non-Billable Charges
      case '1120':
        // Calculate from student charges that are not part of regular fees
        const nonBillableCharges = studentFinance?.students?.reduce((sum, student) => {
          if (student.charges && Array.isArray(student.charges)) {
            return sum + student.charges.reduce((chargeSum, charge) => {
              return chargeSum + (parseFloat(charge.amount) || 0);
            }, 0);
          }
          return sum;
        }, 0) || 0;
        return { debit: nonBillableCharges, credit: 0 };

      // Accounts Receivable - Canteen Fees
      case '1200':
        const canteenFeesOutstanding = canteenFees?.outstandingAmount || 0;
        return { debit: canteenFeesOutstanding, credit: 0 };

      // Inventory - Products (use real products stock)
      case '1300': {
        const productList = Array.isArray(products) ? products : [];

        // Inventory = Σ (sellingPrice * stockQuantity) for active products
        const inventoryFromProducts = productList.reduce((sum, product) => {
          const isActive = product.isActive !== false; // treat missing as active
          if (!isActive) return sum;

          const qty = parseFloat(product.stockQuantity) || 0;
          const sellingPrice =
            parseFloat(product.sellingPrice) ||
            parseFloat(product.price) || // fallback if you use `price`
            0;

          // If either is zero, contribute nothing
          if (!qty || !sellingPrice) return sum;

          return sum + qty * sellingPrice;
        }, 0);

        // If we have valid product-based inventory, use it
        if (inventoryFromProducts > 0) {
          return { debit: inventoryFromProducts, credit: 0 };
        }

        // Fallback: legacy sales-based approximation (kept to avoid breaking if products are empty)
        const inventoryValue = Array.isArray(sales)
          ? sales.reduce((sum, sale) => {
              if (
                sale.category === 'canteen' ||
                sale.type === 'canteen' ||
                sale.itemType === 'canteen'
              ) {
                const quantity = parseFloat(sale.quantity) || 0;
                const unitCost =
                  parseFloat(sale.unitPrice) ||
                  parseFloat(sale.cost) ||
                  0;
                return sum + quantity * unitCost;
              }
              return sum;
            }, 0)
          : 0;

        return { debit: inventoryValue, credit: 0 };
      }

      // Prepaid Expenses
      case '1400':
        const prepaidExpenses = this.categorizeExpenses(expenditure?.expenses || [], 'prepaid');
        return { debit: prepaidExpenses, credit: 0 };

      // Fixed Assets
      case '1500':
      case '1600':
      case '1700':
        const fixedAssets = this.calculateFixedAssetsValue(assets?.assets || [], account.accountCode);
        return { debit: fixedAssets, credit: 0 };

      // Accumulated Depreciation
      case '1800':
        const accumulatedDepreciation = this.calculateAccumulatedDepreciation(assets?.assets || []);
        return { debit: 0, credit: accumulatedDepreciation };

      // Liabilities
      case '2000': // Accounts Payable
        const accountsPayable = expenditure?.expenses?.reduce((sum, expense) => {
          // Expenses that haven't been paid yet (if there's a payment status field)
          const amount = parseFloat(expense.amountspent) || 0;
          return sum + amount;
        }, 0) || 0;
        return { debit: 0, credit: accountsPayable };

      case '2100': // Accrued Salaries
        const accruedSalaries = salaries?.totalSalaries || 0;
        return { debit: 0, credit: accruedSalaries };

      case '2200': // Accrued Expenses
        const accruedExpenses = expenditure?.totalExpenses || 0;
        return { debit: 0, credit: accruedExpenses };

      case '2300': // Short-term Debt
        return { debit: 0, credit: 0 }; // Would need debt data

      case '2400': // Long-term Debt
        return { debit: 0, credit: 0 }; // Would need debt data

      // Equity
      case '3000': // Capital
        return { debit: 0, credit: 0 }; // Would need capital data

      case '3100': // Retained Earnings
        // Calculate from net income (revenue - expenses)
        const totalRevenue = (studentFinance?.paidFees || 0) + (canteenFees?.totalRevenue || 0);
        const totalExpensesForEquity = (expenditure?.totalExpenses || 0) + (salaries?.totalSalaries || 0);
        const netIncome = totalRevenue - totalExpensesForEquity;
        return { debit: 0, credit: Math.max(0, netIncome) };

      // Revenue Accounts - All should show credit balances
      case '4000': // School Fees Revenue
        const schoolFeesRevenue = studentFinance?.paidFees || 0; // Use paid fees, not total fees
        return { debit: 0, credit: schoolFeesRevenue };

      case '4100': // Canteen Revenue
        const canteenRevenue = canteenFees?.totalRevenue || 0;
        return { debit: 0, credit: canteenRevenue };

      case '4200': // Admission Fee Revenue
        const admissionFees = this.calculateChargeRevenue(studentFinance?.students || [], 'admission');
        return { debit: 0, credit: admissionFees };

      case '4210': // Printing Fee Revenue
        const printingFees = this.calculateChargeRevenue(studentFinance?.students || [], 'printing');
        return { debit: 0, credit: printingFees };

      case '4220': // Books Revenue
        const booksRevenue = this.calculateChargeRevenue(studentFinance?.students || [], 'book');
        return { debit: 0, credit: booksRevenue };

      case '4230': // Uniform Revenue
        const uniformRevenue = this.calculateChargeRevenue(studentFinance?.students || [], 'uniform');
        return { debit: 0, credit: uniformRevenue };

      case '4240': // Other Charges Revenue
        const otherCharges = this.calculateChargeRevenue(studentFinance?.students || [], 'other');
        return { debit: 0, credit: otherCharges };

      case '4250': // Daily Charges Revenue
        const dailyCharges = this.calculateChargeRevenue(studentFinance?.students || [], 'daily');
        return { debit: 0, credit: dailyCharges };

      case '4260': // Weekly Charges Revenue
        const weeklyCharges = this.calculateChargeRevenue(studentFinance?.students || [], 'weekly');
        return { debit: 0, credit: weeklyCharges };

      case '4270': // Monthly Charges Revenue
        const monthlyCharges = this.calculateChargeRevenue(studentFinance?.students || [], 'monthly');
        return { debit: 0, credit: monthlyCharges };

      case '4280': // Termly Charges Revenue
        const termlyCharges = this.calculateChargeRevenue(studentFinance?.students || [], 'termly');
        return { debit: 0, credit: termlyCharges };

      case '4300': // Discounts Given (Contra Revenue - Debit balance)
        const discounts = studentFinance?.students?.reduce((sum, student) => {
          if (student.finance && student.finance.length > 0) {
            const latestFinance = student.finance[student.finance.length - 1];
            if (latestFinance.payment && Array.isArray(latestFinance.payment)) {
              const discountAmount = latestFinance.payment
                .filter(p => p.des === 'Discount' || p.des === 'discount')
                .reduce((discSum, p) => discSum + (parseFloat(p.amountpaid) || 0), 0);
              return sum + discountAmount;
            }
          }
          return sum;
        }, 0) || 0;
        return { debit: discounts, credit: 0 };

      // Expense Accounts - All should show debit balances
      case '5000': // Salaries and Wages
        const totalSalaries = salaries?.totalSalaries || 0;
        return { debit: totalSalaries, credit: 0 };

      case '5100': // Administrative Expenses
        const adminExpenses = this.categorizeExpenses(expenditure?.expenses || [], 'administrative');
        return { debit: adminExpenses, credit: 0 };

      case '5200': // Utilities
        const utilities = this.categorizeExpenses(expenditure?.expenses || [], 'utilities');
        return { debit: utilities, credit: 0 };

      case '5300': // Maintenance and Repairs
        const maintenance = this.categorizeExpenses(expenditure?.expenses || [], 'maintenance');
        return { debit: maintenance, credit: 0 };

      case '5400': // Professional Services
        const professionalServices = this.categorizeExpenses(expenditure?.expenses || [], 'professional');
        return { debit: professionalServices, credit: 0 };

      case '5500': // Office Supplies
        const officeSupplies = this.categorizeExpenses(expenditure?.expenses || [], 'office');
        return { debit: officeSupplies, credit: 0 };

      case '5600': // Transportation
        const transportation = this.categorizeExpenses(expenditure?.expenses || [], 'transport');
        return { debit: transportation, credit: 0 };

      case '5700': // Insurance
        const insurance = this.categorizeExpenses(expenditure?.expenses || [], 'insurance');
        return { debit: insurance, credit: 0 };

      case '5800': // Depreciation Expense
        const depreciationExpense = this.calculateDepreciationExpense(assets?.assets || []);
        return { debit: depreciationExpense, credit: 0 };

      // Default case
      default:
        return { debit: 0, credit: 0 };
    }
  }

  // Helper method to categorize expenses
  categorizeExpenses(expenses, keyword) {
    if (!Array.isArray(expenses) || expenses.length === 0) return 0;
    return expenses
      .filter(expense => {
        const purpose = (expense.purpose || '').toLowerCase();
        const description = (expense.description || '').toLowerCase();
        const category = (expense.category || '').toLowerCase();
        const searchTerm = keyword.toLowerCase();
        return purpose.includes(searchTerm) || 
               description.includes(searchTerm) ||
               category.includes(searchTerm);
      })
      .reduce((sum, expense) => sum + (parseFloat(expense.amountspent) || 0), 0);
  }

  // Helper method to calculate charge revenue from student charges
  calculateChargeRevenue(students, chargeType) {
    if (!Array.isArray(students) || students.length === 0) return 0;
    return students.reduce((sum, student) => {
      if (student.charges && Array.isArray(student.charges)) {
        const matchingCharges = student.charges.filter(c => {
          const type = (c.type || '').toLowerCase();
          const description = (c.description || '').toLowerCase();
          const chargeName = (c.name || '').toLowerCase();
          const searchTerm = chargeType.toLowerCase();
          return type.includes(searchTerm) || 
                 description.includes(searchTerm) ||
                 chargeName.includes(searchTerm);
        });
        return sum + matchingCharges.reduce((chargeSum, charge) => {
          return chargeSum + (parseFloat(charge.amount) || 0);
        }, 0);
      }
      return sum;
    }, 0);
  }

  // Calculate fixed assets value
  calculateFixedAssetsValue(assets, accountCode) {
    const assetTypeMap = {
      '1500': ['Building'], // Land and Buildings
      '1600': ['Equipment', 'Technology'], // Equipment
      '1700': ['Furniture', 'Books'] // Furniture and Fixtures
    };

    const assetTypes = assetTypeMap[accountCode] || [];
    return assets
      .filter(asset => assetTypes.includes(asset.assetType))
      .reduce((sum, asset) => sum + (asset.currentValue || asset.purchaseCost || 0), 0);
  }

  // Calculate accumulated depreciation
  calculateAccumulatedDepreciation(assets) {
    return assets.reduce((sum, asset) => {
      const purchaseCost = asset.purchaseCost || 0;
      const currentValue = asset.currentValue || 0;
      const depreciation = Math.max(0, purchaseCost - currentValue);
      return sum + depreciation;
    }, 0);
  }

  // Calculate depreciation expense
  calculateDepreciationExpense(assets) {
    // Calculate annual depreciation based on asset age and depreciation rate
    const currentYear = new Date().getFullYear();
    return assets.reduce((sum, asset) => {
      const purchaseYear = new Date(asset.purchaseDate).getFullYear();
      const age = currentYear - purchaseYear;
      const depreciationRate = asset.depreciationRate || 0.1; // 10% default
      const annualDepreciation = (asset.purchaseCost || 0) * depreciationRate;
      return sum + (annualDepreciation * age);
    }, 0);
  }

  // Balance Sheet Account Mapping Configuration
  // This maps balance sheet line items to Chart of Accounts codes
  // Can be customized per school or deployment
  getBalanceSheetMapping() {
    return {
      assets: {
        currentAssets: {
          cash: ['1000'], // Cash and Cash Equivalents
          accountsReceivable: ['1100', '1110', '1120', '1200'], // All receivables
          inventory: ['1300'], // Inventory
          prepaidExpenses: ['1400'] // Prepaid Expenses
        },
        fixedAssets: {
          landBuildings: ['1500'], // Land and Buildings
          equipment: ['1600'], // Equipment
          furniture: ['1700'], // Furniture and Fixtures
          accumulatedDepreciation: ['1800'] // Accumulated Depreciation (credit)
        }
      },
      liabilities: {
        currentLiabilities: {
          accountsPayable: ['2000'], // Accounts Payable
          accruedSalaries: ['2100'], // Accrued Salaries
          accruedExpenses: ['2200'], // Accrued Expenses
          shortTermDebt: ['2300'] // Short-term Debt
        },
        longTermLiabilities: {
          longTermDebt: ['2400'] // Long-term Debt
        }
      },
      equity: {
        capital: ['3000'], // Capital (can be configured)
        retainedEarnings: ['3100'] // Retained Earnings (calculated from net income)
      }
    };
  }

  // Calculate balance sheet section from account codes
  calculateBalanceSheetSection(accountCodes, financialData, accountType) {
    try {
      // Handle empty account codes
      if (!accountCodes || accountCodes.length === 0) {
        return { total: 0 };
      }

      let totalDebit = 0;
      let totalCredit = 0;
      const lineItems = {};

      accountCodes.forEach(accountCode => {
        try {
          // Create a temporary account object for the calculation
          const account = {
            accountCode: accountCode,
            accountName: `Account ${accountCode}`,
            accountType: accountType,
            normalBalance: accountType === 'Asset' ? 'Debit' : 
                          accountType === 'Liability' || accountType === 'Equity' ? 'Credit' : 'Debit'
          };

          // Calculate balance using existing function
          const balance = this.calculateAccountBalanceFromRealData(account, financialData);
          
          // Ensure balance is valid
          if (!balance || typeof balance.debit !== 'number' || typeof balance.credit !== 'number') {
            console.warn(`Invalid balance for account ${accountCode}:`, balance);
            return; // Skip this account
          }
          
          // Store line item
          lineItems[accountCode] = {
            debit: balance.debit || 0,
            credit: balance.credit || 0,
            net: accountType === 'Asset' ? (balance.debit || 0) - (balance.credit || 0) :
                 accountType === 'Liability' || accountType === 'Equity' ? (balance.credit || 0) - (balance.debit || 0) : 0
          };

          totalDebit += balance.debit || 0;
          totalCredit += balance.credit || 0;
        } catch (err) {
          console.error(`Error calculating balance for account ${accountCode}:`, err);
          // Continue with other accounts
        }
      });

      // For assets: use debit - credit
      // For liabilities/equity: use credit - debit
      const total = accountType === 'Asset' 
        ? totalDebit - totalCredit 
        : totalCredit - totalDebit;

      return {
        ...lineItems,
        total: Math.max(0, total) // Ensure non-negative
      };
    } catch (error) {
      console.error('Error in calculateBalanceSheetSection:', error);
      return { total: 0 };
    }
  }

  // Calculate balance sheet from account codes (flexible approach)
  calculateBalanceSheetFromAccounts(financialData, chartOfAccounts, netIncome) {
    try {
      const mapping = this.getBalanceSheetMapping();
      
      // Get account codes from chart of accounts if available
      const getAccountCodes = (codes) => {
        if (!codes || codes.length === 0) {
          return [];
        }
        
        if (!chartOfAccounts || chartOfAccounts.length === 0) {
          return codes; // Use default codes if no chart of accounts
        }
        
        // Find accounts in chart of accounts that match the codes
        const foundCodes = chartOfAccounts
          .filter(acc => acc && acc.accountCode && codes.includes(acc.accountCode))
          .map(acc => acc.accountCode);
        
        // If no matches found, use default codes
        return foundCodes.length > 0 ? foundCodes : codes;
      };

    // Calculate Current Assets
    const currentAssets = {
      cash: this.calculateBalanceSheetSection(
        getAccountCodes(mapping.assets.currentAssets.cash),
        financialData,
        'Asset'
      ).total,
      receivables: this.calculateBalanceSheetSection(
        getAccountCodes(mapping.assets.currentAssets.accountsReceivable),
        financialData,
        'Asset'
      ).total,
      inventory: this.calculateBalanceSheetSection(
        getAccountCodes(mapping.assets.currentAssets.inventory),
        financialData,
        'Asset'
      ).total,
      prepaid: this.calculateBalanceSheetSection(
        getAccountCodes(mapping.assets.currentAssets.prepaidExpenses),
        financialData,
        'Asset'
      ).total
    };
    currentAssets.total = currentAssets.cash + currentAssets.receivables + 
                          currentAssets.inventory + currentAssets.prepaid;

    // Calculate Fixed Assets
    const fixedAssets = {
      landBuildings: this.calculateBalanceSheetSection(
        getAccountCodes(mapping.assets.fixedAssets.landBuildings),
        financialData,
        'Asset'
      ).total,
      equipment: this.calculateBalanceSheetSection(
        getAccountCodes(mapping.assets.fixedAssets.equipment),
        financialData,
        'Asset'
      ).total,
      furniture: this.calculateBalanceSheetSection(
        getAccountCodes(mapping.assets.fixedAssets.furniture),
        financialData,
        'Asset'
      ).total,
      depreciation: this.calculateBalanceSheetSection(
        getAccountCodes(mapping.assets.fixedAssets.accumulatedDepreciation),
        financialData,
        'Asset'
      ).total // This will be negative (credit balance)
    };
    fixedAssets.total = fixedAssets.landBuildings + fixedAssets.equipment + 
                        fixedAssets.furniture - Math.abs(fixedAssets.depreciation);

    // Calculate Current Liabilities
    const currentLiabilities = {
      payables: this.calculateBalanceSheetSection(
        getAccountCodes(mapping.liabilities.currentLiabilities.accountsPayable),
        financialData,
        'Liability'
      ).total,
      accrued: this.calculateBalanceSheetSection(
        getAccountCodes(mapping.liabilities.currentLiabilities.accruedExpenses),
        financialData,
        'Liability'
      ).total,
      shortTermDebt: this.calculateBalanceSheetSection(
        getAccountCodes(mapping.liabilities.currentLiabilities.shortTermDebt),
        financialData,
        'Liability'
      ).total
    };
    currentLiabilities.total = currentLiabilities.payables + 
                               currentLiabilities.accrued + 
                               currentLiabilities.shortTermDebt;

    // Calculate Long-term Liabilities
    const longTermLiabilities = {
      longTermDebt: this.calculateBalanceSheetSection(
        getAccountCodes(mapping.liabilities.longTermLiabilities.longTermDebt),
        financialData,
        'Liability'
      ).total
    };
    longTermLiabilities.total = longTermLiabilities.longTermDebt;

    // Calculate Equity
    // Capital - use account code if available, otherwise use calculated value
    const capitalBalance = this.calculateBalanceSheetSection(
      getAccountCodes(mapping.equity.capital),
      financialData,
      'Equity'
    ).total;
    
    // Retained Earnings = Previous + Net Income
    const retainedEarningsBalance = this.calculateBalanceSheetSection(
      getAccountCodes(mapping.equity.retainedEarnings),
      financialData,
      'Equity'
    ).total;
    
    const equity = {
      capital: capitalBalance || 0, // Use calculated or default to 0
      retainedEarnings: retainedEarningsBalance + (netIncome || 0),
      currentYearEarnings: netIncome || 0
    };
    equity.totalEquity = equity.capital + equity.retainedEarnings;

    return {
      assets: {
        currentAssets,
        fixedAssets,
        totalAssets: currentAssets.total + fixedAssets.total
      },
      liabilities: {
        currentLiabilities,
        longTermLiabilities,
        totalLiabilities: currentLiabilities.total + longTermLiabilities.total
      },
      equity
    };
    } catch (error) {
      console.error('Error in calculateBalanceSheetFromAccounts:', error);
      // Return minimal structure on error
      return {
        assets: {
          currentAssets: { cash: 0, receivables: 0, inventory: 0, prepaid: 0, total: 0 },
          fixedAssets: { landBuildings: 0, equipment: 0, furniture: 0, depreciation: 0, total: 0 },
          totalAssets: 0
        },
        liabilities: {
          currentLiabilities: { payables: 0, accrued: 0, shortTermDebt: 0, total: 0 },
          longTermLiabilities: { longTermDebt: 0, total: 0 },
          totalLiabilities: 0
        },
        equity: { capital: 0, retainedEarnings: 0, currentYearEarnings: netIncome || 0, total: 0 }
      };
    }
  }

  // Generate Balance Sheet from real data
  async generateRealBalanceSheet(schoolID, startDate, endDate, reduxData, chartOfAccounts = null) {
    try {
      // Ensure dates are Date objects
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      // Validate date range
      if (start > end) {
        throw new Error('Start date must be before end date');
      }
      
      if (!schoolID) {
        throw new Error('School ID is required');
      }
      
      console.log('Generating balance sheet for school:', schoolID);
      console.log('Date range:', start.toISOString().split('T')[0], 'to', end.toISOString().split('T')[0]);
      
      const financialData = await accountingDataService.getAllFinancialData(
        schoolID, 
        start, 
        end, 
        reduxData
      );
      
      console.log('Financial data retrieved:', {
        hasStudentFinance: !!financialData.studentFinance,
        hasCanteenFees: !!financialData.canteenFees,
        hasSalaries: !!financialData.salaries,
        hasExpenditure: !!financialData.expenditure
      });

      // If chart of accounts is provided, use it; otherwise use default structure
      const accounts = chartOfAccounts || this.createChartOfAccountsFromReduxData(reduxData);

      const calculations = financialCalculationService;

      // Discounts given on school fees (contra revenue / reduces AR).
      const discountsGiven = (financialData?.studentFinance?.students || []).reduce((sum, student) => {
        const financeList = Array.isArray(student?.finance) ? student.finance : [];
        if (financeList.length === 0) return sum;
        const latestFinance = financeList[financeList.length - 1];
        const payments = Array.isArray(latestFinance?.payment) ? latestFinance.payment : [];
        const studentDiscounts = payments
          .filter((p) => String(p?.des || '').trim().toLowerCase() === 'discount')
          .reduce((inner, p) => inner + (parseFloat(p?.amountpaid) || 0), 0);
        return sum + studentDiscounts;
      }, 0);

      // Use adjusted financial snapshot so assets and equity both reflect discounts.
      const adjustedFinancialData = {
        ...financialData,
        studentFinance: {
          ...(financialData?.studentFinance || {}),
          outstandingFees: Math.max(
            0,
            (financialData?.studentFinance?.outstandingFees || 0) - discountsGiven
          ),
        },
      };
      
      // Calculate revenue and expenses for net income
      const revenue = calculations.calculateTotalRevenue(adjustedFinancialData);
      const expenses = calculations.calculateTotalExpenses(adjustedFinancialData);
      const netIncome = revenue.total - expenses.total - discountsGiven;
      
      // Build balance sheet using flexible account-based approach
      // Wrap in try-catch with fallback to old method if new one fails
      let balanceSheetData;
      try {
        console.log('Attempting to generate balance sheet using flexible account-based approach...');
        balanceSheetData = this.calculateBalanceSheetFromAccounts(
          adjustedFinancialData,
          accounts,
          netIncome
        );
        console.log('Balance sheet data calculated successfully:', balanceSheetData);
      } catch (error) {
        console.error('Error with flexible approach, falling back to traditional method:', error);
        // Fallback to traditional calculation method
        balanceSheetData = {
          assets: {
            currentAssets: calculations.calculateCurrentAssets(adjustedFinancialData) || {},
            fixedAssets: calculations.calculateFixedAssets(adjustedFinancialData) || {},
            totalAssets: 0
          },
          liabilities: {
            currentLiabilities: calculations.calculateCurrentLiabilities(adjustedFinancialData) || {},
            longTermLiabilities: calculations.calculateLongTermLiabilities(adjustedFinancialData) || {},
            totalLiabilities: 0
          },
          equity: calculations.calculateEquity(adjustedFinancialData, netIncome) || { capital: 0, retainedEarnings: 0, totalEquity: 0 }
        };
        
        // Calculate totals with fallbacks
        balanceSheetData.assets.totalAssets = 
          (balanceSheetData.assets.currentAssets?.total || 0) + 
          (balanceSheetData.assets.fixedAssets?.total || 0);
        balanceSheetData.liabilities.totalLiabilities = 
          (balanceSheetData.liabilities.currentLiabilities?.total || 0) + 
          (balanceSheetData.liabilities.longTermLiabilities?.total || 0);
        balanceSheetData.equity.totalEquity = 
          (balanceSheetData.equity.capital || 0) + 
          (balanceSheetData.equity.retainedEarnings || 0);
      }
      
      // Build complete balance sheet structure
      const balanceSheet = {
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0],
        asOfDate: end.toISOString().split('T')[0],
        generatedAt: new Date(),
        ...balanceSheetData,
        adjustments: {
          discountsGiven
        },
        accounts: accounts ? accounts.filter(acc => 
          acc.accountType === 'Asset' || 
          acc.accountType === 'Liability' || 
          acc.accountType === 'Equity'
        ) : []
      };
      
      // Ensure totalEquity is set if missing
      if (!balanceSheet.equity.totalEquity && balanceSheet.equity.totalEquity !== 0) {
        balanceSheet.equity.totalEquity = 
          (balanceSheet.equity.capital || 0) + 
          (balanceSheet.equity.retainedEarnings || 0);
      }
      
      // Ensure totalLiabilities is set if missing
      if (!balanceSheet.liabilities.totalLiabilities && balanceSheet.liabilities.totalLiabilities !== 0) {
        balanceSheet.liabilities.totalLiabilities = 
          (balanceSheet.liabilities.currentLiabilities?.total || 0) + 
          (balanceSheet.liabilities.longTermLiabilities?.total || 0);
      }
      
      // Ensure totalAssets is set if missing
      if (!balanceSheet.assets.totalAssets && balanceSheet.assets.totalAssets !== 0) {
        balanceSheet.assets.totalAssets = 
          (balanceSheet.assets.currentAssets?.total || 0) + 
          (balanceSheet.assets.fixedAssets?.total || 0);
      }
      
      // Verify balance: Assets = Liabilities + Equity
      const totalLiabilitiesAndEquity = 
        (balanceSheet.liabilities.totalLiabilities || 0) + 
        (balanceSheet.equity.totalEquity || 0);
      
      const totalAssets = balanceSheet.assets.totalAssets || 0;
      
      balanceSheet.balanced = Math.abs(totalAssets - totalLiabilitiesAndEquity) < 0.01;
      balanceSheet.difference = totalAssets - totalLiabilitiesAndEquity;
      
      console.log('Balance sheet generated:', {
        period: `${start.toISOString().split('T')[0]} to ${end.toISOString().split('T')[0]}`,
        totalAssets: balanceSheet.assets.totalAssets,
        totalLiabilities: balanceSheet.liabilities.totalLiabilities,
        totalEquity: balanceSheet.equity.totalEquity,
        balanced: balanceSheet.balanced,
        difference: balanceSheet.difference
      });
      
      return balanceSheet;
    } catch (error) {
      console.error('Error generating real balance sheet:', error);
      // Return a minimal structure instead of throwing
      return {
        startDate: startDate,
        endDate: endDate,
        asOfDate: endDate,
        generatedAt: new Date(),
        assets: { currentAssets: {}, fixedAssets: {}, totalAssets: 0 },
        liabilities: { currentLiabilities: {}, longTermLiabilities: {}, totalLiabilities: 0 },
        equity: { capital: 0, retainedEarnings: 0, totalEquity: 0 },
        error: error.message,
        balanced: false,
        difference: 0
      };
    }
  }

  // Generate Income Statement from real data
  async generateRealIncomeStatement(schoolID, startDate, endDate, reduxData) {
    try {
      const financialData = await accountingDataService.getAllFinancialData(
        schoolID, 
        new Date(startDate), 
        new Date(endDate), 
        reduxData
      );

      const calculations = financialCalculationService;
      
      return {
        period: { startDate, endDate },
        revenue: calculations.calculateRevenue(financialData),
        expenses: calculations.calculateExpenses(financialData),
        netIncome: calculations.calculateNetIncome(financialData)
      };
    } catch (error) {
      console.error('Error generating real income statement:', error);
      throw error;
    }
  }

  // Generate Cash Flow Statement from real data
  async generateRealCashFlowStatement(schoolID, startDate, endDate, reduxData) {
    try {
      console.log('Generating real cash flow statement for school:', schoolID);
      console.log('Date range:', startDate, 'to', endDate);
      
      // Get all financial data filtered by date range
      const financialData = await accountingDataService.getAllFinancialData(
        schoolID, 
        new Date(startDate), 
        new Date(endDate), 
        reduxData
      );

      console.log('Financial data received:', {
        studentFinance: financialData?.studentFinance?.paidFees || 0,
        canteenFees: financialData?.canteenFees?.totalRevenue || 0,
        sales: financialData?.sales?.totalRevenue || 0,
        expenses: financialData?.expenditure?.totalExpenses || 0,
        salaries: financialData?.salaries?.totalSalaries || 0
      });

      // Calculate net income from income statement
      const incomeStatement = financialCalculationService.generateIncomeStatement(financialData);
      const netIncome = incomeStatement.netIncome || 0;

      // Calculate depreciation from assets
      const depreciation = this.calculateDepreciationExpense(reduxData.assets || []);

      // Calculate working capital changes
      // Accounts Receivable Change = (Beginning AR - Ending AR)
      // For simplicity, use outstanding fees as AR
      const accountsReceivable = financialData?.studentFinance?.outstandingFees || 0;
      const accountsReceivableChange = -accountsReceivable * 0.1; // Assume 10% change

      // Inventory Change (if we have inventory data)
      const inventoryChange = 0; // Can be calculated from sales/inventory if available

      // Accounts Payable Change
      const accountsPayable = financialData?.expenditure?.totalExpenses || 0;
      const accountsPayableChange = accountsPayable * 0.1; // Assume 10% change

      // Track school-fees discounts in period (informational contra-cash indicator).
      const parseReportDateMs = (dateValue) => {
        const raw = String(dateValue || '').trim();
        if (!raw) return NaN;
        const ymd = raw.includes('T') ? raw.split('T')[0] : raw;
        const parts = ymd.split('-');
        if (parts.length < 3) return NaN;
        const year = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const day = parseInt(parts[2], 10);
        if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return NaN;
        return new Date(year, month - 1, day, 12, 0, 0, 0).getTime();
      };
      const startMs = parseReportDateMs(startDate);
      const endMs = parseReportDateMs(endDate);
      let discountsGiven = 0;
      const students = Array.isArray(reduxData?.students) ? reduxData.students : [];
      for (const student of students) {
        const financeList = Array.isArray(student?.finance) ? student.finance : [];
        for (const financeEntry of financeList) {
          const payments = Array.isArray(financeEntry?.payment) ? financeEntry.payment : [];
          for (const payment of payments) {
            const paymentMs = parseReportDateMs(payment?.date);
            if (!Number.isFinite(paymentMs) || !Number.isFinite(startMs) || !Number.isFinite(endMs)) continue;
            if (paymentMs < startMs || paymentMs > endMs) continue;
            if (String(payment?.des || '').trim().toLowerCase() !== 'discount') continue;
            discountsGiven += parseFloat(payment?.amountpaid) || 0;
          }
        }
      }

      // Operating Activities Total
      const operatingTotal = netIncome + depreciation + accountsReceivableChange + 
                            inventoryChange + accountsPayableChange;

      // Investing Activities - from expenses categorized as building/equipment
      const equipmentPurchase = -this.categorizeExpenses(reduxData.expenses || [], 'equipment');
      const buildingImprovements = -this.categorizeExpenses(reduxData.expenses || [], 'building');
      const investingTotal = equipmentPurchase + buildingImprovements;

      // Financing Activities - typically loans and capital contributions
      // These would come from loan records if available, for now use 0
      const loanProceeds = 0;
      const loanPayments = 0;
      const capitalContributions = 0;
      const financingTotal = loanProceeds + loanPayments + capitalContributions;

      // Net Cash Change
      const netCashChange = operatingTotal + investingTotal + financingTotal;

      const cashFlowStatement = {
        period: { startDate, endDate },
        generatedAt: new Date(),
        operatingActivities: {
          netIncome: netIncome,
          depreciation: depreciation,
          accountsReceivableChange: accountsReceivableChange,
          inventoryChange: inventoryChange,
          accountsPayableChange: accountsPayableChange,
          discountsGiven: discountsGiven
        },
        investingActivities: {
          equipmentPurchase: equipmentPurchase,
          buildingImprovements: buildingImprovements
        },
        financingActivities: {
          loanProceeds: loanProceeds,
          loanPayments: loanPayments,
          capitalContributions: capitalContributions
        },
        operatingTotal: operatingTotal,
        investingTotal: investingTotal,
        financingTotal: financingTotal,
        netCashChange: netCashChange
      };

      console.log('Cash flow statement generated:', cashFlowStatement);
      return cashFlowStatement;
    } catch (error) {
      console.error('Error generating real cash flow statement:', error);
      // Return minimal structure instead of throwing
      return {
        period: { startDate, endDate },
        generatedAt: new Date(),
        operatingActivities: {
          netIncome: 0,
          depreciation: 0,
          accountsReceivableChange: 0,
          inventoryChange: 0,
          accountsPayableChange: 0,
          discountsGiven: 0
        },
        investingActivities: {
          equipmentPurchase: 0,
          buildingImprovements: 0
        },
        financingActivities: {
          loanProceeds: 0,
          loanPayments: 0,
          capitalContributions: 0
        },
        operatingTotal: 0,
        investingTotal: 0,
        financingTotal: 0,
        netCashChange: 0,
        error: error.message
      };
    }
  }

  // Create journal entries from real transactions
  async createJournalEntriesFromTransactions(schoolID, startDate, endDate, reduxData) {
    try {
      const financialData = await accountingDataService.getAllFinancialData(
        schoolID, 
        new Date(startDate), 
        new Date(endDate), 
        reduxData
      );

      const journalEntries = [];

      // School Fees Collection Entry
      if (financialData.studentFinance?.paidFees > 0) {
        journalEntries.push({
          description: 'School Fees Collection',
          reference: `SF-${new Date().toISOString().split('T')[0]}`,
          date: new Date().toISOString().split('T')[0],
          entries: [
            {
              accountCode: '1000',
              accountName: 'Cash and Cash Equivalents',
              description: 'Cash received from school fees',
              debit: financialData.studentFinance.paidFees,
              credit: 0
            },
            {
              accountCode: '4000',
              accountName: 'School Fees Revenue',
              description: 'School fees revenue earned',
              debit: 0,
              credit: financialData.studentFinance.paidFees
            }
          ],
          schoolID,
          createdBy: 'system',
          userName: 'System'
        });
      }

      // Canteen Revenue Entry
      if (financialData.canteenFees?.totalRevenue > 0) {
        journalEntries.push({
          description: 'Canteen Revenue',
          reference: `CR-${new Date().toISOString().split('T')[0]}`,
          date: new Date().toISOString().split('T')[0],
          entries: [
            {
              accountCode: '1000',
              accountName: 'Cash and Cash Equivalents',
              description: 'Cash received from canteen sales',
              debit: financialData.canteenFees.totalRevenue,
              credit: 0
            },
            {
              accountCode: '4100',
              accountName: 'Canteen Revenue',
              description: 'Canteen revenue earned',
              debit: 0,
              credit: financialData.canteenFees.totalRevenue
            }
          ],
          schoolID,
          createdBy: 'system',
          userName: 'System'
        });
      }

      // Salaries Payment Entry
      if (financialData.salaries?.totalSalaries > 0) {
        journalEntries.push({
          description: 'Salaries Payment',
          reference: `SP-${new Date().toISOString().split('T')[0]}`,
          date: new Date().toISOString().split('T')[0],
          entries: [
            {
              accountCode: '5000',
              accountName: 'Salaries and Wages',
              description: 'Salaries and wages expense',
              debit: financialData.salaries.totalSalaries,
              credit: 0
            },
            {
              accountCode: '1000',
              accountName: 'Cash and Cash Equivalents',
              description: 'Cash paid for salaries',
              debit: 0,
              credit: financialData.salaries.totalSalaries
            }
          ],
          schoolID,
          createdBy: 'system',
          userName: 'System'
        });
      }

      // Operating Expenses Entry
      if (financialData.expenditure?.totalExpenses > 0) {
        journalEntries.push({
          description: 'Operating Expenses',
          reference: `OE-${new Date().toISOString().split('T')[0]}`,
          date: new Date().toISOString().split('T')[0],
          entries: [
            {
              accountCode: '5100',
              accountName: 'Administrative Expenses',
              description: 'Administrative expenses incurred',
              debit: financialData.expenditure.totalExpenses,
              credit: 0
            },
            {
              accountCode: '1000',
              accountName: 'Cash and Cash Equivalents',
              description: 'Cash paid for expenses',
              debit: 0,
              credit: financialData.expenditure.totalExpenses
            }
          ],
          schoolID,
          createdBy: 'system',
          userName: 'System'
        });
      }

      // Create journal entries
      const createdEntries = [];
      for (const entry of journalEntries) {
        try {
          const createdEntry = await accountingService.createJournalEntry(entry);
          createdEntries.push(createdEntry);
        } catch (error) {
          console.error('Error creating journal entry:', error);
        }
      }

      return createdEntries;
    } catch (error) {
      console.error('Error creating journal entries from transactions:', error);
      throw error;
    }
  }

  // Get comprehensive financial dashboard data
  async getRealDashboardData(schoolID, reduxData) {
    try {
      console.log('Getting real dashboard data for school:', schoolID);
      console.log('Redux data available:', {
        students: reduxData.students?.length || 0,
        teachers: reduxData.teachers?.length || 0,
        expenses: reduxData.expenses?.length || 0,
        sales: reduxData.sales?.length || 0,
        assets: reduxData.assets?.length || 0
      });

      // Calculate real financial metrics directly from Redux data
      console.log('Redux data structure check:', {
        students: reduxData.students ? reduxData.students.length : 0,
        teachers: reduxData.teachers ? reduxData.teachers.length : 0,
        expenses: reduxData.expenses ? reduxData.expenses.length : 0,
        sales: reduxData.sales ? reduxData.sales.length : 0,
        assets: reduxData.assets ? reduxData.assets.length : 0
      });

      const studentFinance = this.calculateStudentFinanceMetrics(reduxData.students || [], reduxData.class || []);
      const canteenFees = this.calculateCanteenFeesMetrics(reduxData.students || []);
      const salaries = this.calculateSalariesMetrics(reduxData.teachers || []);
      const expenditure = this.calculateExpenditureMetrics(reduxData.expenses || []);
      const sales = this.calculateSalesMetrics(reduxData.sales || []);
      const assets = this.calculateAssetsMetrics(reduxData.assets || []);

      const totalRevenue = studentFinance.paidFees + canteenFees.totalRevenue + sales.totalRevenue;
      const totalExpenses = expenditure.totalExpenses + salaries.totalSalaries;
      const netIncome = totalRevenue - totalExpenses;
      const cashBalance = 50000 + totalRevenue - totalExpenses; // Base cash + revenue - expenses

      console.log('Calculated metrics:', {
        totalRevenue,
        totalExpenses,
        netIncome,
        cashBalance,
        studentFinance,
        canteenFees,
        salaries,
        expenditure,
        sales,
        assets
      });

      return {
        keyMetrics: {
          totalRevenue,
          totalExpenses,
          netIncome,
          cashBalance
        },
        studentFinance,
        canteenFees,
        salaries,
        expenditure,
        sales,
        assets,
        revenueTrends: this.calculateRevenueTrends({
          studentFinance,
          canteenFees,
          sales
        }),
        expenseBreakdown: this.calculateExpenseBreakdown({
          salaries,
          expenditure
        }),
        alerts: this.generateFinancialAlerts({
          studentFinance,
          canteenFees,
          salaries,
          expenditure,
          sales,
          assets
        }),
        lastUpdated: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error getting real dashboard data:', error);
      throw error;
    }
  }

  // Calculate revenue trends
  calculateRevenueTrends(financialData) {
    return [
      { month: 'Jan', schoolFees: financialData.studentFinance?.totalFees * 0.8 || 0, canteenFees: financialData.canteenFees?.totalRevenue * 0.8 || 0 },
      { month: 'Feb', schoolFees: financialData.studentFinance?.totalFees * 0.9 || 0, canteenFees: financialData.canteenFees?.totalRevenue * 0.9 || 0 },
      { month: 'Mar', schoolFees: financialData.studentFinance?.totalFees || 0, canteenFees: financialData.canteenFees?.totalRevenue || 0 }
    ];
  }

  // Calculate expense breakdown
  calculateExpenseBreakdown(financialData) {
    return {
      salaries: financialData.salaries?.totalSalaries || 0,
      administrative: this.categorizeExpenses(financialData.expenditure?.expenses || [], 'administrative'),
      utilities: this.categorizeExpenses(financialData.expenditure?.expenses || [], 'utilities'),
      maintenance: this.categorizeExpenses(financialData.expenditure?.expenses || [], 'maintenance'),
      other: this.categorizeExpenses(financialData.expenditure?.expenses || [], 'other')
    };
  }

  // Calculate student finance metrics directly from Redux data
  // Updated to use individual bills when available
  calculateStudentFinanceMetrics(students, classData = []) {
    console.log('Calculating student finance metrics from:', students.length, 'students');
    console.log('Class data available:', classData.length, 'classes');
    
    let totalFees = 0;
    let paidFees = 0;
    let outstandingFees = 0;
    let paidStudents = 0;
    let outstandingStudents = 0;
    let studentsWithIndividualBills = 0;

    students.forEach((student, index) => {
      console.log(`Student ${index + 1}:`, {
        id: student.ID || student._id,
        name: `${student.lastname || ''} ${student.othernames || ''}`,
        class: student.class_,
        hasFinance: !!(student.finance && student.finance.length > 0),
        financeLength: student.finance ? student.finance.length : 0
      });

      // Get class data for this student
      const classMatch = classData.filter(cls => cls.classname === student.class_);
      const studentClass = classMatch.length > 0 ? classMatch[0] : null;
      const classFees = studentClass ? parseFloat(studentClass.classfees) || 0 : 0;
      
      console.log(`Class fees for ${student.class_}:`, classFees);

      if (student.finance && student.finance.length > 0) {
        const latestFinance = student.finance[student.finance.length - 1];
        console.log('Latest finance data:', latestFinance);
        
        // Get term from latest finance record
        let term = null;
        if (latestFinance.term) {
          term = typeof latestFinance.term === 'object' 
            ? (latestFinance.term.name || latestFinance.term.term || latestFinance.term.value)
            : latestFinance.term;
        }
        
        // Use bill utility to get correct bill (individual -> class -> default)
        let totalStudentFees = 0;
        if (studentClass && term) {
          totalStudentFees = calculateTotalFeesDue(student, studentClass, term);
          const bill = getStudentBill(student, studentClass, term);
          if (bill && bill.source === 'individual') {
            studentsWithIndividualBills++;
          }
        } else {
          // Fallback to original logic if no term/class data
        const totalFeesAmount = parseFloat(latestFinance.totalfees) || 0;
        const arrears = parseFloat(latestFinance.Arrears) || 0;
        const otherCharges = parseFloat(latestFinance.othercharges) || 0;
          totalStudentFees = totalFeesAmount > 0 ? totalFeesAmount : (classFees + arrears + otherCharges);
        }
        
        totalFees += totalStudentFees;
        
        // Calculate paid amount from payment array
        if (latestFinance.payment && latestFinance.payment.length > 0) {
          const totalPaid = latestFinance.payment.reduce((sum, payment) => {
            if (payment.des !== "Discount") {
              return sum + (parseFloat(payment.amountpaid) || 0);
            }
            return sum;
          }, 0);
          paidFees += totalPaid;
          console.log(`Student ${index + 1} paid:`, totalPaid);
        }
        
        // Calculate outstanding (balance)
        const balance = parseFloat(latestFinance.balance) || 0;
        outstandingFees += balance;

        if (balance === 0) {
          paidStudents++;
        } else {
          outstandingStudents++;
        }
      } else {
        // Student has no finance data, use class fees as total
        totalFees += classFees;
        outstandingFees += classFees;
        outstandingStudents++;
        console.log(`Student ${index + 1} has no finance data, using class fees:`, classFees);
      }
    });

    console.log('Student finance calculation results:', {
      totalFees,
      paidFees,
      outstandingFees,
      paidStudents,
      outstandingStudents,
      collectionRate: totalFees > 0 ? (paidFees / totalFees) * 100 : 0
    });

    return {
      totalFees,
      paidFees,
      outstandingFees,
      paidStudents,
      outstandingStudents,
      collectionRate: totalFees > 0 ? (paidFees / totalFees) * 100 : 0
    };
  }

  // Calculate canteen fees metrics
  calculateCanteenFeesMetrics(students) {
    console.log('Calculating canteen fees metrics from:', students.length, 'students');
    
    let totalRevenue = 0;
    let outstandingAmount = 0;

    students.forEach((student, index) => {
      console.log(`Student ${index + 1} canteen data:`, {
        hasCanteen: !!(student.canteen && student.canteen.length > 0),
        canteenLength: student.canteen ? student.canteen.length : 0,
        dailyCharge: student.dailycharge,
        weeklyCharge: student.weeklycharge,
        monthlyCharge: student.monthlycharge
      });

      // Check for canteen payments in finance array
      if (student.finance && student.finance.length > 0) {
        student.finance.forEach(finance => {
          if (finance.payment && finance.payment.length > 0) {
            finance.payment.forEach(payment => {
              if (payment.des === "Canteen" || payment.des === "Canteen Fees" || payment.des === "canteen") {
                const amount = parseFloat(payment.amountpaid) || 0;
                totalRevenue += amount;
                console.log('Found canteen payment:', amount);
              }
            });
          }
        });
      }

      // Check for canteen array
      if (student.canteen && student.canteen.length > 0) {
        student.canteen.forEach(canteenRecord => {
          const amount = parseFloat(canteenRecord.amount) || 0;
          const paid = parseFloat(canteenRecord.paid) || 0;
          totalRevenue += paid;
          outstandingAmount += (amount - paid);
        });
      }

      // Check for daily/weekly/monthly charges
      if (student.dailycharge) {
        const dailyAmount = parseFloat(student.dailycharge) || 0;
        totalRevenue += dailyAmount * 30; // Monthly calculation
        console.log('Found daily charge:', dailyAmount);
      }
      if (student.weeklycharge) {
        const weeklyAmount = parseFloat(student.weeklycharge) || 0;
        totalRevenue += weeklyAmount * 4; // Monthly calculation
        console.log('Found weekly charge:', weeklyAmount);
      }
      if (student.monthlycharge) {
        const monthlyAmount = parseFloat(student.monthlycharge) || 0;
        totalRevenue += monthlyAmount;
        console.log('Found monthly charge:', monthlyAmount);
      }
    });

    console.log('Canteen fees calculation results:', {
      totalRevenue,
      outstandingAmount
    });

    return {
      totalRevenue,
      outstandingAmount,
      totalTransactions: students.reduce((sum, student) => 
        sum + (student.canteen ? student.canteen.length : 0), 0
      )
    };
  }

  // Calculate salaries metrics
  calculateSalariesMetrics(teachers) {
    let totalSalaries = 0;
    let paidSalaries = 0;
    let outstandingSalaries = 0;

    teachers.forEach(teacher => {
      const salary = parseFloat(teacher.salary) || 0;
      const paid = parseFloat(teacher.paid) || 0;
      totalSalaries += salary;
      paidSalaries += paid;
      outstandingSalaries += (salary - paid);
    });

    return {
      totalSalaries,
      paidSalaries,
      outstandingSalaries,
      totalTeachers: teachers.length
    };
  }

  // Calculate expenditure metrics
  calculateExpenditureMetrics(expenses) {
    let totalExpenses = 0;
    let paidExpenses = 0;
    let outstandingExpenses = 0;

    expenses.forEach(expense => {
      const amount = parseFloat(expense.amountspent) || 0;
      const paid = parseFloat(expense.paid) || 0;
      totalExpenses += amount;
      paidExpenses += paid;
      outstandingExpenses += (amount - paid);
    });

    return {
      totalExpenses,
      paidExpenses,
      outstandingExpenses,
      totalTransactions: expenses.length
    };
  }

  // Calculate sales metrics
  calculateSalesMetrics(sales) {
    console.log('Calculating sales metrics from:', sales.length, 'sales');
    console.log('Sales data sample:', sales.slice(0, 2));
    
    let totalRevenue = 0;
    let totalTransactions = 0;

    sales.forEach((sale, index) => {
      console.log(`Sale ${index + 1}:`, {
        total: sale.total,
        subtotal: sale.subtotal,
        discount: sale.discount,
        tax: sale.tax,
        items: sale.items ? sale.items.length : 0
      });

      const amount = parseFloat(sale.total) || 0;
      totalRevenue += amount;
      totalTransactions++;
    });

    console.log('Sales calculation results:', {
      totalRevenue,
      totalTransactions
    });

    return {
      totalRevenue,
      totalTransactions,
      averageTransaction: totalTransactions > 0 ? totalRevenue / totalTransactions : 0
    };
  }

  // Calculate assets metrics
  calculateAssetsMetrics(assets) {
    let totalValue = 0;
    let totalCost = 0;
    let totalDepreciation = 0;

    assets.forEach(asset => {
      const cost = parseFloat(asset.purchaseCost) || 0;
      const value = parseFloat(asset.currentValue) || 0;
      const depreciation = parseFloat(asset.depreciation) || 0;
      
      totalCost += cost;
      totalValue += value;
      totalDepreciation += depreciation;
    });

    return {
      totalValue,
      totalCost,
      totalDepreciation,
      totalAssets: assets.length
    };
  }

  // Generate financial alerts
  generateFinancialAlerts(financialData) {
    const alerts = [];
    
    if (financialData.studentFinance?.outstandingFees > 50000) {
      alerts.push({
        severity: 'warning',
        message: `High outstanding school fees: ${accountingService.formatCurrency(financialData.studentFinance.outstandingFees)}`
      });
    }
    
    if (financialData.canteenFees?.outstandingAmount > 10000) {
      alerts.push({
        severity: 'warning',
        message: `High outstanding canteen fees: ${accountingService.formatCurrency(financialData.canteenFees.outstandingAmount)}`
      });
    }
    
    const netIncome = (financialData.studentFinance?.totalFees || 0) + (financialData.canteenFees?.totalRevenue || 0) - 
                     (financialData.salaries?.totalSalaries || 0) - (financialData.expenditure?.totalExpenses || 0);
    
    if (netIncome < 0) {
      alerts.push({
        severity: 'error',
        message: `Negative net income: ${accountingService.formatCurrency(netIncome)}`
      });
    }
    
    return alerts;
  }

  // Get comprehensive financial data
  async getComprehensiveFinancialData(schoolID, startDate, endDate, reduxData) {
    try {
      return await comprehensiveAccountingDataService.getComprehensiveFinancialData(
        schoolID, startDate, endDate, reduxData
      );
    } catch (error) {
      console.error('Error getting comprehensive financial data:', error);
      throw error;
    }
  }

  // Generate comprehensive financial reports
  // Updated to include individual bill breakdown
  async generateComprehensiveFinancialReports(schoolID, startDate, endDate, reduxData) {
    try {
      const financialData = await this.getComprehensiveFinancialData(schoolID, startDate, endDate, reduxData);
      
      // Calculate individual vs class bill breakdown for school fees
      const students = reduxData.students || [];
      const classes = reduxData.class || [];
      let individualBillsRevenue = 0;
      let classBillsRevenue = 0;
      let studentsWithIndividualBills = 0;
      let studentsWithClassBills = 0;
      
      students.forEach(student => {
        if (student.finance && student.finance.length > 0) {
          const latestFinance = student.finance[student.finance.length - 1];
          if (latestFinance.payment && Array.isArray(latestFinance.payment)) {
            const term = typeof latestFinance.term === 'object' 
              ? (latestFinance.term.name || latestFinance.term.term || latestFinance.term.value)
              : latestFinance.term;
            const studentClass = classes.find(c => c.classname === student.class_);
            
            if (studentClass && term) {
              const bill = getStudentBill(student, studentClass, term);
              if (bill) {
                const totalPaid = latestFinance.payment.reduce((sum, payment) => {
                  if (payment.des !== "Discount") {
                    return sum + (parseFloat(payment.amountpaid) || 0);
                  }
                  return sum;
                }, 0);
                
                if (bill.source === 'individual') {
                  individualBillsRevenue += totalPaid;
                  studentsWithIndividualBills++;
                } else if (bill.source === 'class') {
                  classBillsRevenue += totalPaid;
                  studentsWithClassBills++;
                } else {
                  classBillsRevenue += totalPaid;
                  studentsWithClassBills++;
                }
              }
            }
          }
        }
      });
      
      // Enhanced Income Statement
      const incomeStatement = {
        revenue: {
          schoolFees: Object.values(financialData.classWiseData).reduce((sum, classInfo) => sum + classInfo.schoolFees.total, 0),
          schoolFeesBreakdown: {
            individualBills: individualBillsRevenue,
            classBills: classBillsRevenue,
            studentsWithIndividualBills: studentsWithIndividualBills,
            studentsWithClassBills: studentsWithClassBills
          },
          admissionFees: financialData.chargesData.admissionFees.total,
          printingFees: financialData.chargesData.printingFees.total,
          booksRevenue: financialData.chargesData.booksCharges.total,
          uniformRevenue: financialData.chargesData.uniformCharges.total,
          canteenRevenue: financialData.chargesData.canteenFees.total,
          dailyCharges: financialData.chargesData.dailyCharges.total,
          weeklyCharges: financialData.chargesData.weeklyCharges.total,
          monthlyCharges: financialData.chargesData.monthlyCharges.total,
          termlyCharges: financialData.chargesData.termlyCharges.total,
          otherRevenue: financialData.chargesData.otherCharges.total,
          salesRevenue: financialData.sales?.totalRevenue || 0,
          totalRevenue: 0
        },
        expenses: {
          salaries: financialData.salaries?.totalSalaries || 0,
          administrative: financialData.expenditure?.totalAmount || 0,
          totalExpenses: 0
        },
        adjustments: {
          discountsGiven: financialData.chargesData.discounts.total
        },
        netIncome: 0
      };

      // Calculate totals
      incomeStatement.revenue.totalRevenue = 
        incomeStatement.revenue.schoolFees +
        incomeStatement.revenue.admissionFees +
        incomeStatement.revenue.printingFees +
        incomeStatement.revenue.booksRevenue +
        incomeStatement.revenue.uniformRevenue +
        incomeStatement.revenue.canteenRevenue +
        incomeStatement.revenue.dailyCharges +
        incomeStatement.revenue.weeklyCharges +
        incomeStatement.revenue.monthlyCharges +
        incomeStatement.revenue.termlyCharges +
        incomeStatement.revenue.otherRevenue +
        incomeStatement.revenue.salesRevenue;

      incomeStatement.expenses.totalExpenses = 
        incomeStatement.expenses.salaries + 
        incomeStatement.expenses.administrative;

      incomeStatement.netIncome = 
        incomeStatement.revenue.totalRevenue - 
        incomeStatement.expenses.totalExpenses - 
        incomeStatement.adjustments.discountsGiven;

      // Enhanced Balance Sheet
      const balanceSheet = {
        assets: {
          currentAssets: {
            cash: 50000 + incomeStatement.revenue.totalRevenue, // Base cash + revenue
            accountsReceivable: incomeStatement.revenue.totalRevenue * 0.1, // 10% outstanding
            inventory: financialData.assets?.totalValue || 0
          },
          fixedAssets: {
            landAndBuildings: financialData.assets?.categoryBreakdown?.['Land and Buildings'] || 0,
            equipment: financialData.assets?.categoryBreakdown?.['Equipment'] || 0,
            furniture: financialData.assets?.categoryBreakdown?.['Furniture'] || 0,
            accumulatedDepreciation: 0
          }
        },
        liabilities: {
          currentLiabilities: {
            accountsPayable: incomeStatement.expenses.totalExpenses * 0.2, // 20% unpaid
            accruedExpenses: incomeStatement.expenses.totalExpenses * 0.1 // 10% accrued
          }
        },
        equity: {
          capital: 100000,
          retainedEarnings: incomeStatement.netIncome
        }
      };

      // Class-wise Financial Analysis
      const classWiseAnalysis = Object.values(financialData.classWiseData).map(classInfo => ({
        className: classInfo.classname,
        totalRevenue: 
          classInfo.schoolFees.total +
          classInfo.admissionFees.total +
          classInfo.printingFees.total +
          classInfo.booksCharges.total +
          classInfo.uniformCharges.total +
          classInfo.canteenFees.total +
          classInfo.otherCharges.total,
        breakdown: {
          schoolFees: classInfo.schoolFees.total,
          schoolFeesBreakdown: classInfo.schoolFeesBreakdown || {
            individualBills: 0,
            classBills: 0,
            studentsWithIndividualBills: 0
          },
          admissionFees: classInfo.admissionFees.total,
          printingFees: classInfo.printingFees.total,
          booksCharges: classInfo.booksCharges.total,
          uniformCharges: classInfo.uniformCharges.total,
          canteenFees: classInfo.canteenFees.total,
          otherCharges: classInfo.otherCharges.total,
          discounts: classInfo.discounts.total
        },
        studentCount: classInfo.schoolFees.count,
        averageRevenuePerStudent: 0
      }));

      // Calculate averages
      classWiseAnalysis.forEach(analysis => {
        analysis.averageRevenuePerStudent = 
          analysis.studentCount > 0 ? analysis.totalRevenue / analysis.studentCount : 0;
      });

      return {
        incomeStatement,
        balanceSheet,
        classWiseAnalysis,
        chargesBreakdown: financialData.chargesData,
        period: { startDate, endDate },
        generatedAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error generating comprehensive financial reports:', error);
      throw error;
    }
  }

  // Create comprehensive journal entries
  async createComprehensiveJournalEntries(schoolID, startDate, endDate, reduxData) {
    try {
      return await comprehensiveJournalService.createComprehensiveJournalEntries(
        schoolID, startDate, endDate, reduxData
      );
    } catch (error) {
      console.error('Error creating comprehensive journal entries:', error);
      throw error;
    }
  }
}

export default new RealDataAccountingService();
